import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  getEmbedQueryParamsFromSearchParams,
  resolveEmbedDisplay,
} from "src/utils/embedToken";

const defaultState = {
  hideHeader: false,
  hideFooter: false,
  loading: false,
  display: null,
};

/**
 * Resolve embed header/footer visibility from signed token or legacy query params.
 */
export function useEmbedDisplayOptions() {
  const location = useLocation();
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const [state, setState] = useState(defaultState);

  const display = searchParams.get("display");
  const embedToken = searchParams.get("embed_token");
  const header = searchParams.get("header");
  const footer = searchParams.get("footer");

  const hasEmbedContext =
    display === "embeded" || embedToken || header || footer;

  useEffect(() => {
    if (!hasEmbedContext) {
      setState(defaultState);
      return;
    }

    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true }));

    resolveEmbedDisplay({
      display,
      embed_token: embedToken,
      header,
      footer,
    })
      .then(({ hideHeader, hideFooter }) => {
        if (!cancelled) {
          setState({
            hideHeader,
            hideFooter,
            loading: false,
            display,
          });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setState({
            hideHeader: false,
            hideFooter: false,
            loading: false,
            display,
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [display, embedToken, footer, hasEmbedContext, header]);

  const embedQueryParams = useMemo(
    () => getEmbedQueryParamsFromSearchParams(searchParams),
    [searchParams]
  );

  const embedQueryString = embedQueryParams.toString();

  return {
    ...state,
    embedQueryString,
    embedQueryParams,
    isEmbedded: display === "embeded",
  };
}
